
import { useState, useEffect, useCallback } from 'react';
import translations from '@/translations';
import { commonTranslations } from '@/utils/translationUtils';

export function useTranslation() {
  const [language, setLanguage] = useState<string>('en');
  
  useEffect(() => {
    // Get language from localStorage or fallback to browser language
    const storedLanguage = localStorage.getItem('appLanguage');
    const browserLang = navigator.language?.split('-')[0];
    
    // Check if the browser language is supported in our translations
    const isBrowserLangSupported = browserLang && translations[browserLang];
    
    // Use the first available language in this priority: localStorage > browser lang > 'en'
    const detectedLanguage = storedLanguage || 
                            (isBrowserLangSupported ? browserLang : 'en');
    
    setLanguage(detectedLanguage);
    
    // Set the HTML lang attribute
    document.documentElement.lang = detectedLanguage;
    
    // Listen for language changes
    const handleLanguageChange = (event: CustomEvent<{language: string}>) => {
      setLanguage(event.detail.language);
    };
    
    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange as EventListener);
    };
  }, []);
  
  const t = useCallback((key: string): string => {
    // Check if the key exists in commonTranslations
    if (commonTranslations[key as keyof typeof commonTranslations]) {
      const translation = commonTranslations[key as keyof typeof commonTranslations][language as keyof typeof commonTranslations[keyof typeof commonTranslations]];
      if (translation) {
        return translation;
      }
    }
    
    // Check if the language exists in our translations
    if (!translations[language]) {
      // Fallback to English if the language doesn't exist
      return translations['en'][key] || key;
    }
    
    // Check if the key exists in the selected language
    const translatedText = translations[language][key];
    if (translatedText) {
      return translatedText;
    }
    
    // If not found in selected language, try English
    const englishText = translations['en'][key];
    if (englishText) {
      return englishText;
    }
    
    // If no translation is found anywhere, return the key itself
    console.log(`Translation missing for key: ${key} in language: ${language}`);
    return key;
  }, [language]);
  
  const changeLanguage = useCallback((newLanguage: string) => {
    if (translations[newLanguage]) {
      localStorage.setItem('appLanguage', newLanguage);
      document.documentElement.lang = newLanguage;
      
      // Dispatch a custom event to notify other components
      window.dispatchEvent(new CustomEvent('languageChange', { 
        detail: { language: newLanguage }
      }));
    }
  }, []);
  
  return { t, language, changeLanguage };
}
